import type { StackProps } from '@hover/blueprint';
import {
  Body,
  Box,
  Button,
  HStack,
  Icon,
  VStack,
  useToast,
} from '@hover/blueprint';
import { iAlertCircle, iconScan } from '@hover/icons';
import React from 'react';

import { useErrors } from 'src/modules/homeownerApp/hooks';
import { useRemoveScanFromProjectHandler } from 'src/modules/homeownerApp/hooks/useRemoveScanFromProjectHandler';
import type { Job } from 'src/shared/graphql/generated';
import { TranslationNamespace, useTranslate } from 'src/shared/hooks';

import { PROJECT_DETAILS_PADDING } from '../../Projects/Project/ProjectCardSection';

interface ScanErrorProps extends StackProps {
  scan: Job;
  projectId: string;
}

const ScanError = ({ scan, projectId, ...props }: ScanErrorProps) => {
  const t = useTranslate(TranslationNamespace.HOMEOWNER_APP);
  const toast = useToast();
  const { showError } = useErrors();

  const [removeScanHandler, removeScanResult] = useRemoveScanFromProjectHandler(
    Number(projectId),
    scan,
  );

  const handleStartNewScan = () => {
    // TODO: Implement
  };

  const handleRemoveScan = () => {
    removeScanHandler({
      onSuccess: () => {
        toast({
          props: {
            message: t('customize.newProject.removeScanSuccess'),
            colorScheme: 'dark',
          },
        });
      },
      onError: () => {
        showError({
          error: removeScanResult.error as Error,
          formatError: message =>
            t('projects.confirmation.removeScan.error', { message }),
        });
      },
    });
  };

  return (
    <VStack px={PROJECT_DETAILS_PADDING} {...props}>
      <Box
        backgroundColor="neutral.100"
        borderRadius={350}
        flexDirection="column"
        mb={500}
        px={500}
        py={600}
        width="100%"
      >
        <HStack alignItems="center">
          <Icon icon={iAlertCircle} size="medium" />
          <Body fontWeight={700} m={0}>
            {t('projects.scan.error')}
          </Body>
        </HStack>
        <Body color="neutral.600" mb={0} textStyle="body300">
          {scan.modelFailureReason || t('projects.scan.defaultScanError')}
        </Body>
      </Box>
      <Button
        iconAfter={iconScan}
        onClick={handleStartNewScan}
        size="large"
        width="100%"
      >
        {t('projects.scan.startNewScan')}
      </Button>
      <Button
        onClick={handleRemoveScan}
        size="large"
        variant="secondary"
        width="100%"
      >
        {t('projects.scan.removeScan')}
      </Button>
    </VStack>
  );
};

export { ScanError };
